import React, {useEffect, useRef, useState} from 'react';
import {Loader} from '../../components/Loader';
import PrimaryDocumentExplorePage from "../PrimaryDocumentExplorePage";
import PrimaryDashboardPage from "../PrimaryGroupPage";
import {useEnhancedDocumentsContext} from "../../contexts/doc_contexts";
import {useNavigationContext} from "../../contexts/NavContext/nav-context";
import {useUserContext} from "../../contexts/user_context";
import {useGroupContext} from "../../contexts/group_context";
import {useParams} from "react-router-dom";
import Swal from "sweetalert2";
import {useFolderContext} from "../../contexts/folder_context";
import PrimaryFolderPage from "../PrimaryFoldersPage";
import {validate as validateUUID} from 'uuid';
import {AppView} from '../../App';


export interface HomeProps {
    startingView: AppView;
}

const Home: React.FC<HomeProps> = ({startingView}) => {
    const {docId, folderId, groupId} = useParams<{ docId?: string; folderId?: string; groupId?: string }>();
    const {currentUser} = useUserContext()!;
    const {currentGroup, setCurrentGroup, userGroups} = useGroupContext()!;
    const {groupFolders} = useFolderContext()!;
    const {document, getDocument, loadedDocMap} = useEnhancedDocumentsContext()!;
    const {
        navigateTo,
        currentView,
        navLoading,
        setDocumentAsync,
        setCurrentFolderAsync,
    } = useNavigationContext()!;

    const [loading, setLoading] = useState(true);

    const hasNavigatedRef = useRef(false);

    // Progressive initialization for URL-based navigation.
    //
    // Phase 1: When userGroups loads, ensure the correct group (from URL) is
    //          set as currentGroup. This triggers FolderContext to derive
    //          groupFolders from the RIGHT group.
    // Phase 2: When groupFolders loads (now from the correct group), look up
    //          the folder and complete navigation.
    //
    // For views that don't need folders (FolderView, MyDashboard), we
    // navigate immediately in phase 1. The effect re-runs naturally as
    // each dependency becomes available.
    useEffect(() => {
        if (hasNavigatedRef.current) return;

        const validGroupId = groupId && validateUUID(groupId);
        const validFolderId = folderId && validateUUID(folderId);
        const validDocId = docId && validateUUID(docId);

        const needGroups = [AppView.FolderView, AppView.DocumentSearch, AppView.SingleDocumentView].includes(startingView);
        const needFolders = [AppView.DocumentSearch, AppView.SingleDocumentView].includes(startingView);

        // Gate: wait for user and groups to load
        if (currentUser === undefined || (needGroups && userGroups === undefined)) {
            setLoading(true);
            return;
        }

        if (currentUser === null) {
            navigateTo({view: AppView.Login});
            setLoading(false);
            hasNavigatedRef.current = true;
            return;
        }

        // Phase 1: Ensure correct group is set before looking up folders.
        // setCurrentGroup is synchronous (React state setter) — it triggers
        // FolderContext to derive groupFolders on the next render. We return
        // early and let the effect re-run when groupFolders becomes available.
        if (needGroups && validGroupId) {
            const targetGroup = userGroups!.find(g => g.id === groupId);
            if (!targetGroup) {
                Swal.fire("Invalid URL", "Group not found, redirecting.", "error");
                navigateTo({view: AppView.MyDashboard});
                setLoading(false);
                hasNavigatedRef.current = true;
                return;
            }
            if (currentGroup?.id !== targetGroup.id) {
                // Set the correct group — FolderContext will derive groupFolders
                // from this group on the next render cycle.
                setCurrentGroup(targetGroup);
                return; // Wait for groupFolders to cascade
            }
        }

        // Phase 2: Wait for groupFolders if needed (now derived from correct group)
        if (needFolders && groupFolders === undefined) {
            return;
        }

        // All prerequisites ready — perform navigation
        const doNavigate = async () => {
            hasNavigatedRef.current = true;

            switch (startingView) {
                case AppView.FolderView:
                    if (validGroupId) {
                        const targetGroup = userGroups!.find(g => g.id === groupId);
                        navigateTo({view: AppView.FolderView, group: targetGroup});
                    } else {
                        navigateTo({view: AppView.MyDashboard});
                    }
                    break;

                case AppView.DocumentSearch:
                    if (validGroupId && validFolderId) {
                        const targetGroup = userGroups!.find(g => g.id === groupId);
                        const targetFolder = groupFolders!.find(f => f.id === folderId);
                        if (targetGroup && targetFolder) {
                            await setCurrentFolderAsync(targetFolder);
                            navigateTo({view: AppView.DocumentSearch, group: targetGroup, folder: targetFolder});
                            if (validDocId) {
                                getDocument(docId!).then(doc => {
                                    if (doc) setDocumentAsync(doc);
                                });
                            }
                        } else {
                            Swal.fire("Invalid URL", "Folder not found, redirecting.", "error");
                            navigateTo({view: AppView.MyDashboard});
                        }
                    } else {
                        navigateTo({view: AppView.MyDashboard});
                    }
                    break;

                case AppView.SingleDocumentView:
                    if (validGroupId && validFolderId && validDocId) {
                        let targetDoc = loadedDocMap.get(docId);
                        if (!targetDoc) {
                           targetDoc = await getDocument(docId!);
                        }
                        const targetGroup = userGroups!.find(g => g.id === groupId);
                        const targetFolder = groupFolders!.find(f => f.id === folderId);
                        if (targetGroup && targetFolder && targetDoc) {
                            await setCurrentFolderAsync(targetFolder);
                            await setDocumentAsync(targetDoc);
                            navigateTo({
                                view: AppView.SingleDocumentView,
                                group: targetGroup,
                                folder: targetFolder,
                                doc: targetDoc
                            });
                        } else {
                            console.log(loadedDocMap)
                            console.log(
                                `Invalid IDs - Group: ${groupId}, Folder: ${folderId}, Document: ${docId}`
                            )
                            Swal.fire("Invalid URL", "Document or folder not found, redirecting.", "error");
                            navigateTo({view: AppView.MyDashboard});
                        }
                    } else {
                        navigateTo({view: AppView.MyDashboard});
                    }
                    break;

                default:
                    navigateTo({view: AppView.MyDashboard});
                    break;
            }

            setLoading(false);
        };

        doNavigate();
    }, [
        currentUser,
        currentGroup?.id,
        userGroups,
        groupFolders,
        groupId,
        folderId,
        docId,
        startingView,
    ]);


    const isLoading = loading || navLoading || (userGroups === undefined);

    // Auth guard: Don't render anything until we confirm user is authenticated
    if (currentUser === undefined) {
        return (
            <div className="absolute inset-0 flex justify-center items-center z-50 bg-white bg-opacity-75">
                <Loader text="Verifying authentication..."/>
            </div>
        );
    }

    // Not authenticated - this should trigger redirect in useEffect, but render nothing while it happens
    if (currentUser === null) {
        return null;
    }

    return (
        <>
            {isLoading && (
                <div className="absolute inset-0 flex justify-center items-center z-50 bg-white bg-opacity-75">
                    <Loader text="Loading..."/>
                </div>
            )}

            <div className='flex flex-col w-full h-full'>
                {currentView === AppView.MyDashboard && <PrimaryDashboardPage/>}
                {currentView === AppView.FolderView && <PrimaryFolderPage/>}
                {currentView === AppView.DocumentSearch && <PrimaryDocumentExplorePage/>}
                {currentView === AppView.SingleDocumentView && document && <PrimaryDocumentExplorePage initialDocExpanded={true}/>}
            </div>
        </>
    );
};

export default Home;
